import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PageHeader } from "@/components/shared";
import { MapPin, Phone, MessageCircle, Mail, Clock, Check } from "lucide-react";

const WA = "919115423336";

export default function Contact() {
  useSeo({
    title: "Contact & Location — Family Constellation Therapy, Ludhiana",
    description: "Get in touch with Arjunna Soni's Family Constellation practice on Mall Road, Ludhiana. Call, WhatsApp, or send an enquiry — replies within 24 hours.",
    path: "/contact",
  });
  // Privacy: pause PostHog session recording on this sensitive page.
  useEffect(() => {
    try { window.posthog?.stopSessionRecording?.(); } catch {}
    return () => { try { window.posthog?.startSessionRecording?.(); } catch {} };
  }, []);
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", interest: params.get("interest") || "Individual session",
    message: "", preferredContact: "email", consent: false, website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the consent checkbox."); return; }
    setBusy(true);
    try {
      const res = await api.post("/inquiries", form);
      toast.success(res.data.message);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader crumb="Contact" title="Get in touch" intro="Have a question before you book? I'm glad to answer. I typically reply within 24 hours." />
      <section className="container-page pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {done ? (
              <div className="bg-surface-card border hairline rounded-lg p-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4"><Check className="text-gold-dark" size={26} /></div>
                <h2 className="text-2xl text-earth mb-2" style={{ fontWeight: 600 }}>Thank you — your message is on its way</h2>
                <p className="text-earth/70 mb-6">I typically reply within 24 hours. While you wait, you might enjoy reading the journal.</p>
                <Link to="/resources" className="btn-outline">Read the journal</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="label-caps text-earth/60 block mb-2">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="contact-name" /></div>
                  <div><label className="label-caps text-earth/60 block mb-2">Phone</label><input required placeholder="+91…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="contact-phone" /></div>
                </div>
                <div><label className="label-caps text-earth/60 block mb-2">Email</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="contact-email" /></div>
                <div><label className="label-caps text-earth/60 block mb-2">I'm interested in</label>
                  <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="contact-interest">
                    <option>Individual session</option><option>Workshop</option><option>Corporate or group</option><option>Other</option>
                  </select>
                </div>
                <div><label className="label-caps text-earth/60 block mb-2">Message</label><textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-md bg-surface-card border hairline outline-none focus:border-gold" data-testid="contact-message" /></div>
                <div><label className="label-caps text-earth/60 block mb-2">Preferred contact method</label>
                  <div className="flex gap-4">
                    {["email", "phone", "whatsapp"].map((m) => (
                      <label key={m} className="flex items-center gap-2 text-earth/80 capitalize cursor-pointer"><input type="radio" name="pc" checked={form.preferredContact === m} onChange={() => setForm({ ...form, preferredContact: m })} className="accent-[#C9A84C]" />{m}</label>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-earth/70 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-[#C9A84C]" data-testid="contact-consent" />
                  <span>I consent to my details being used to respond to this enquiry, per the privacy policy.</span>
                </label>
                <button type="submit" disabled={busy} className="btn-gold" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-4">
            <div className="bg-surface-card border hairline rounded-lg p-7">
              <h3 className="text-xl text-earth mb-4" style={{ fontWeight: 600 }}>Reach me directly</h3>
              <div className="space-y-3 text-earth/80">
                <p className="flex items-start gap-3"><MapPin size={18} className="text-gold shrink-0 mt-1" /> Holistic Healing and Wellness, 81/7 Mall Road, Opp Sutluj Club Main Gate, Civil Lines, Ludhiana (Pb)-141001</p>
                <a href="tel:+919115423336" className="flex items-center gap-3 hover:text-gold-dark"><Phone size={18} className="text-gold" /> +91 9115423336</a>
                <a href="mailto:hello@arjunnasoni.in" className="flex items-center gap-3 hover:text-gold-dark"><Mail size={18} className="text-gold" /> hello@arjunnasoni.in</a>
                <p className="flex items-center gap-3 text-earth/60"><Clock size={18} className="text-gold" /> I typically reply within 24 hours.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <a href="tel:+919115423336" className="btn-outline text-sm" data-testid="click-to-call"><Phone size={16} className="mr-2" /> Call</a>
                <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Hi Arjunna, I'd like to know more about constellation work.")}`} target="_blank" rel="noreferrer" className="btn-gold text-sm" data-testid="click-to-whatsapp"><MessageCircle size={16} className="mr-2" /> WhatsApp</a>
              </div>
            </div>
            <iframe
              title="Map to Holistic Healing and Wellness, Mall Road, Ludhiana"
              src="https://www.google.com/maps?q=Mall+Road+Civil+Lines+Ludhiana&output=embed"
              className="w-full aspect-[2/1] rounded-lg border hairline"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <p className="text-xs text-earth/50 mt-2">Free parking is available nearby on Mall Road. Look for the Holistic Healing and Wellness signage opposite the Sutluj Club main gate.</p>
          </aside>
        </div>
      </section>
    </div>
  );
}
